import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createSlots as _createSlots, createVNode as _createVNode, createBlock as _createBlock, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-wrap w-full mb-2 md:flex-nowrap align-items-center justify-content-end md:justify-content-between" }
const _hoisted_2 = {
  key: 0,
  class: "mr-auto caption"
}
const _hoisted_3 = { class: "flex w-full align-items-center justify-content-between" }

import BigNumber from 'bignumber.js';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { Pair } from '@/sdk/entities/pair';
import { Farm } from '@/sdk/entities/farm';
import { PortfolioFarm } from '@/sdk/entities/portfolioFarm';
import { FarmSourceType } from '@/sdk/entities/farmSource';
import { DEFAULT_CARDANO_CHAIN_ID } from '@/constants/DEFAULT_CARDANO_ID';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import compareMixin from '@/mixins/compare.mixin';
import { fromWei, toWei } from '@/sdk/utils';
import {
  minDecimalsFromCardanoAndMilkomeda,
  normalizeWeiByMinDecimalsWhenExistCardanoToken,
} from '@/helpers/milkomeda-wrapped-smartcontract/milkomeda-wsc-calculation';
import TokenPairIcon from '@/components/token/TokenPairIcon.vue';
import InputBalance from '@/components/form/InputBalance.vue';
import BaseInputNumber from '@/components/form/BaseInputNumber.vue';
import { useBalances } from '@/store/modules/tokens/useBalances';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useMilkomedaWSCBridgeCalculations } from '@/composables/milkomeda-wrapped-smartcontract/useMilkomedaWSCBridgeCalculations';


export default /*#__PURE__*/_defineComponent({
  __name: 'InputPanel',
  props: {
    action: null,
    tokens: null,
    farm: null,
    disabled: { type: Boolean },
    inputValue: null,
    isResetInput: { type: Boolean },
    onAfterReset: null
  },
  emits: ["hasValidationError", "inputChanged"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'hasValidationError', value: { hasError: boolean }): void;
  (e: 'inputChanged', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  action: 'stake' | 'unstake';
  tokens: Pair;
  farm: Farm | PortfolioFarm;
  disabled: boolean;
  inputValue: string;
  isResetInput: boolean;
  onAfterReset: () => void;
}

const { eq } = compareMixin.methods;

const { t } = useI18n();
const { state } = useStore();
const { isPresentTokenIntoNetwork } = useTokens();
const { balanceByTokenSymbolAndChainId } = useBalances();
const { estimateMaxBridgeAmountFromCardano } = useMilkomedaWSCBridgeCalculations();

const EMPTY_BALANCE = '0';





const isPanelFocus = ref(false);
const isBalanceError = ref(false);
const focus = ref(false);
const internalValue = ref('');
const maxAmount = ref(EMPTY_BALANCE);

const lpToken = computed(() => {
  return props.farm.liquidityToken;
});
const maxFractionDigits = computed(() => {
  if (!lpToken.value) {
    return 2;
  }

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    return minDecimalsFromCardanoAndMilkomeda(lpToken.value);
  }

  return lpToken.value.decimals;
});

const isPairFarm = computed(() => props.farm.sourceType === FarmSourceType.PAIR);

// BALANCE
const isBalanceTooltip = computed(() => {
  return (
    props.action === 'stake' &&
    ENABLE_FAKE_CARDANO_NETWORK &&
    isPresentTokenIntoNetwork(lpToken.value.symbol!, +DEFAULT_CARDANO_CHAIN_ID!)
  );
});
const balanceLPInCardano = computed(() => {
  if (!ENABLE_FAKE_CARDANO_NETWORK) {
    return EMPTY_BALANCE;
  }

  const { symbol } = lpToken.value;

  if (!isPresentTokenIntoNetwork(symbol!, +DEFAULT_CARDANO_CHAIN_ID!)) {
    return EMPTY_BALANCE;
  }

  const rawBalance = balanceByTokenSymbolAndChainId(symbol!, +DEFAULT_CARDANO_CHAIN_ID!).value
    ?.balance;
  return rawBalance?.toExact() || EMPTY_BALANCE;
});
const balanceLPInCardanoFixed = computed(() => BigNumber(balanceLPInCardano.value).toFixed(2));
const balanceLPInMilkomeda = computed(() => {
  return props.action === 'stake'
    ? props.farm.tokensOwned.toExact()
    : props.farm.printDeposited.toExact();
});
const balanceLPInMilkomedaFixed = computed(() => BigNumber(balanceLPInMilkomeda.value).toFixed(2));
// NOTE
// When WSC we can stake LP from Cardano and Milkomeda
const balance = computed(() => {
  return props.action === 'stake'
    ? BigNumber(balanceLPInCardano.value).plus(balanceLPInMilkomeda.value).toFixed()
    : balanceLPInMilkomeda.value;
});

function clearInput() {
  internalValue.value = '';
  props.onAfterReset();
  emits('hasValidationError', {
    hasError: true,
  });
}

function setPanelFocus(value) {
  isPanelFocus.value = value;
}

function onChangeInput(value) {
  internalValue.value = value;
  if (props.isResetInput) {
    clearInput();
  } else {
    isBalanceError.value = false;
    let hasBalanceError = false;
    let hasValidationError = true;
    let hasDecimalsError = false;
    if (internalValue.value !== '') {
      const returnValue = new BigNumber(internalValue.value);
      const minAmount = new BigNumber(10).exponentiatedBy(-lpToken.value.decimals);
      hasValidationError = returnValue.lt(minAmount);
      isBalanceError.value = hasBalanceError = returnValue.gt(balance.value);
      emits('inputChanged', internalValue.value);
    }
    if (internalValue.value === '') {
      emits('inputChanged', internalValue.value);
    }
    emits('hasValidationError', {
      hasError: hasValidationError || hasDecimalsError || hasBalanceError,
    });
  }
  emits('inputChanged', internalValue.value);
}

function onSetMax() {
  if (props.isResetInput) {
    clearInput();
  }
  internalValue.value = maxAmount.value;
  onChangeInput(internalValue.value);
}

async function calculateMaxAmountForStake(): Promise<string> {
  const maxBalanceInMilkomeda = balanceLPInMilkomeda.value;
  const maxBalanceInCardano = await estimateMaxBridgeAmountFromCardano(lpToken.value);

  const maxBalanceForStake = BigNumber(maxBalanceInCardano ?? EMPTY_BALANCE).plus(
    maxBalanceInMilkomeda,
  );

  return maxBalanceForStake.toFixed();
}

async function calculateMaxAmount() {
  let maxBalance = balance.value;

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    if (props.action === 'stake') {
      maxBalance = await calculateMaxAmountForStake();
    }

    const maxBalanceByMinDecimalsInWei = normalizeWeiByMinDecimalsWhenExistCardanoToken(
      toWei(maxBalance, lpToken.value.decimals),
      lpToken.value,
    );

    maxBalance = fromWei(maxBalanceByMinDecimalsInWei, lpToken.value.decimals).toFixed();
  }

  return maxBalance;
}

watch(
  () => props.action,
  () => {
    onChangeInput(internalValue.value);
  },
);

watch(
  () => props.inputValue,
  value => {
    onChangeInput(value);
  },
);

watch(focus, () => {
  if (props.isResetInput) {
    clearInput();
  }
});

// Update max amount
watch(
  async () => await calculateMaxAmount(),
  async maxAmountPromise => {
    maxAmount.value = await maxAmountPromise;
  },
  {
    immediate: true,
  },
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["h-full flex-column input-panel", { 'panel-focus': isPanelFocus.value, 'panel-error': isBalanceError.value }])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (!_unref(state).layout.isMobile)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_unref(t)(__props.action)), 1))
        : _createCommentVNode("", true),
      _createVNode(InputBalance, {
        "balance-value": _unref(balance),
        caption: `${_unref(t)('lpTokens')}`,
        "is-balance-error": isBalanceError.value,
        onSetMax: onSetMax,
        "is-max-disabled": _unref(eq)(_unref(balance), 0) || __props.disabled,
        "lp-balance": ""
      }, _createSlots({ _: 2 }, [
        (_unref(isBalanceTooltip))
          ? {
              name: "tooltip",
              fn: _withCtx(() => [
                _createElementVNode("div", null, [
                  _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.inChain', { chain: 'Cardano' })) + ": ", 1),
                  _createElementVNode("span", null, _toDisplayString(_unref(balanceLPInCardanoFixed)), 1)
                ]),
                _createElementVNode("div", null, [
                  _createTextVNode(_toDisplayString(_unref(t)('wrappedContract.inChain', { chain: 'Milkomeda' })) + ": ", 1),
                  _createElementVNode("span", null, _toDisplayString(_unref(balanceLPInMilkomedaFixed)), 1)
                ])
              ])
            }
          : undefined
      ]), 1032, ["balance-value", "caption", "is-balance-error", "is-max-disabled"])
    ]),
    _createElementVNode("div", _hoisted_3, [
      (!_unref(state).layout.isMobile)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (_unref(isPairFarm))
              ? (_openBlock(), _createBlock(TokenPairIcon, {
                  key: 0,
                  tokenA: __props.tokens.token0,
                  tokenB: __props.tokens.token1,
                  class: "panel-label",
                  full: ""
                }, null, 8, ["tokenA", "tokenB"]))
              : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true),
      _createVNode(BaseInputNumber, {
        modelValue: internalValue.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((internalValue).value = $event)),
        maxFractionDigits: _unref(maxFractionDigits),
        disabled: __props.disabled,
        onInput: _cache[1] || (_cache[1] = ($event: any) => (onChangeInput($event))),
        onFocus: _cache[2] || (_cache[2] = ($event: any) => (setPanelFocus(true))),
        onBlur: _cache[3] || (_cache[3] = ($event: any) => (setPanelFocus(false))),
        class: "flex-grow-1",
        "input-class": "input-xl ml-s3"
      }, null, 8, ["modelValue", "maxFractionDigits", "disabled"])
    ])
  ], 2))
}
}

})